import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createMatch } from "./api/matchesApi";
import LocationMapPicker from "./components/LocationMapPicker";
import { sanitizeApiMessage } from "./auth/clientAuth";
import { DEFAULT_SPORT, SPORTS } from "./sportsOptions";

function combineDateAndTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Local calendar date + clock time → Date (same parsing as combineDateAndTime for comparisons). */
function parseLocalStart(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function todayLocalDateString() {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const day = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function currentLocalTimeHHMM() {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
}

function CreateMatch() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    sport: DEFAULT_SPORT,
    location: "",
    latitude: null,
    longitude: null,
    placeId: "",
    date: "",
    startTime: "",
    endTime: "",
    slots: "",
    matchTotalAmount: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!success) return undefined;
    const t = window.setTimeout(() => {
      navigate("/");
    }, 900);
    return () => window.clearTimeout(t);
  }, [success, navigate]);

  const onLocationChange = useCallback((next) => {
    setForm((prev) => ({
      ...prev,
      ...(next.lat !== undefined
        ? { latitude: next.lat === null || Number.isNaN(next.lat) ? null : next.lat }
        : {}),
      ...(next.lng !== undefined
        ? { longitude: next.lng === null || Number.isNaN(next.lng) ? null : next.lng }
        : {}),
      ...(next.address !== undefined && next.address !== ""
        ? { location: next.address }
        : {}),
      ...(next.placeId !== undefined ? { placeId: next.placeId } : {})
    }));
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const lat = form.latitude;
    const lng = form.longitude;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      setError("Pick a venue on the map or enter latitude and longitude.");
      return;
    }
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      setError("Invalid coordinates.");
      return;
    }
    if (!form.location.trim()) {
      setError("Enter a venue name or search for one on the map.");
      return;
    }

    const startIso = combineDateAndTime(form.date, form.startTime);
    const endIso = combineDateAndTime(form.date, form.endTime);
    const dateIso = form.date ? new Date(`${form.date}T12:00:00`).toISOString() : null;

    if (!startIso || !endIso || !dateIso) {
      setError("Choose a valid date and time range.");
      return;
    }

    const startLocal = parseLocalStart(form.date, form.startTime);
    const endLocal = parseLocalStart(form.date, form.endTime);
    const now = new Date();
    if (startLocal && startLocal.getTime() <= now.getTime()) {
      setError("Match start must be after the current time (not now or in the past).");
      return;
    }
    if (endLocal && startLocal && endLocal.getTime() <= startLocal.getTime()) {
      setError("End time must be after start time.");
      return;
    }

    const slots = parseInt(form.slots, 10);
    if (!Number.isFinite(slots) || slots < 1 || slots > 100) {
      setError("Enter slots between 1 and 100.");
      return;
    }

    const amtRaw = form.matchTotalAmount.trim();
    let matchTotalAmount = 0;
    if (amtRaw !== "") {
      matchTotalAmount = parseFloat(amtRaw);
      if (!Number.isFinite(matchTotalAmount) || matchTotalAmount < 0 || matchTotalAmount > 100000000) {
        setError("Match amount must be between 0 and 100,000,000.");
        return;
      }
    }

    setLoading(true);
    try {
      const { response, data } = await createMatch({
        sport: form.sport,
        location: form.location.trim(),
        latitude: lat,
        longitude: lng,
        placeId: form.placeId || "",
        date: dateIso,
        startTime: startIso,
        endTime: endIso,
        slotsAvailable: slots,
        matchTotalAmount
      });

      if (response.status === 401) {
        setError("Session expired. Log in again.");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        const raw =
          typeof data === "object" && data !== null && "message" in data
            ? data.message
            : "Could not create match.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Network error. Is the API running?");
    } finally {
      setLoading(false);
    }
  };

  const minDate = todayLocalDateString();
  const minStartTime = form.date === minDate ? currentLocalTimeHHMM() : undefined;

  return (
    <main className="page">
      <section className="panel form-panel">
        <h1>Host a Match</h1>
        <p className="muted">
          Choose the venue on the map so players can find it easily.{" "}
          <Link to="/matches">Browse matches</Link>
        </p>

        <form onSubmit={handleSubmit}>
          <label>Sport Selection</label>
          <select value={form.sport} onChange={(e) => setField("sport", e.target.value)}>
            {SPORTS.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>

          <label>Location name</label>
          <input
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            placeholder="Venue name or address"
            required
          />

          <label className="map-picker-section-label">Venue on map</label>
          <LocationMapPicker
            latitude={form.latitude}
            longitude={form.longitude}
            venueLabel={form.location}
            onLocationChange={onLocationChange}
          />

          <label>Date</label>
          <input
            type="date"
            min={minDate}
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
            required
          />

          <label>Start Time</label>
          <input
            type="time"
            min={minStartTime}
            value={form.startTime}
            onChange={(e) => setField("startTime", e.target.value)}
            required
          />

          <label>End Time</label>
          <input
            type="time"
            value={form.endTime}
            onChange={(e) => setField("endTime", e.target.value)}
            required
          />

          <label>Slots</label>
          <input type="number" min="1" max="100" value={form.slots} onChange={(e) => setField("slots", e.target.value)} required />

          <label>Match amount (total pool)</label>
          <input
            type="number"
            min="0"
            max="100000000"
            step="0.01"
            value={form.matchTotalAmount}
            onChange={(e) => setField("matchTotalAmount", e.target.value)}
            placeholder="0 — split evenly across all player slots"
          />
          <p className="muted small">
            Optional. Total cost or pool for the match; each player’s share is this total divided by the number of slots
            (open + booked).
          </p>

          {error ? <p className="error-text">{error}</p> : null}
          {success ? (
            <p className="success-text">Match created successfully. You can share it from the match list.</p>
          ) : null}

          <button className="primary-btn" type="submit" disabled={loading || success}>
            {loading ? "Creating…" : "Create Match"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default CreateMatch;
